import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "../../../language/i18n";
import { useApp } from "../../../context/AppContextProvider";
import { useTour } from "./tourContext";
import { TourPlacement } from "./tourTypes";

interface BubblePos {
  top: number;
  left: number;
}

const SPOT_PAD = 8; // breathing room around the highlighted element
const GAP = 14; // distance from element to bubble
const MARGIN = 8; // min distance from the viewport edge
const MAX_RETRY = 90; // ~1.5s of rAF retries — enough for a navigated screen to
// mount its anchors before a genuinely-absent target is auto-skipped

// Pick the bubble side (preferring `pref`, flipping if it would overflow) then
// clamp it fully inside the viewport.
const computeBubble = (
  rect: DOMRect,
  bw: number,
  bh: number,
  pref: TourPlacement,
): BubblePos => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const cand: Record<string, BubblePos> = {
    right: { left: rect.right + GAP, top: cy - bh / 2 },
    left: { left: rect.left - GAP - bw, top: cy - bh / 2 },
    bottom: { top: rect.bottom + GAP, left: cx - bw / 2 },
    top: { top: rect.top - GAP - bh, left: cx - bw / 2 },
  };
  const fits = (p: BubblePos) =>
    p.top >= MARGIN &&
    p.left >= MARGIN &&
    p.top + bh <= vh - MARGIN &&
    p.left + bw <= vw - MARGIN;
  const fallback = ["bottom", "right", "top", "left"];
  const order =
    pref === "auto" || !cand[pref]
      ? fallback
      : [pref, ...fallback.filter((p) => p !== pref)];
  let chosen = cand[order[0]];
  for (const p of order) {
    if (fits(cand[p])) {
      chosen = cand[p];
      break;
    }
  }
  return {
    top: Math.min(Math.max(chosen.top, MARGIN), vh - bh - MARGIN),
    left: Math.min(Math.max(chosen.left, MARGIN), vw - bw - MARGIN),
  };
};

const TourOverlay = () => {
  const { isActive, activeTour, stepIndex, next, prev, stop } = useTour();
  const { boarding } = useApp();
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [bubble, setBubble] = useState<BubblePos | null>(null);

  const visible = isActive && boarding === "DASHBOARD";
  const step = activeTour?.steps[stepIndex];
  const total = activeTour?.steps.length || 0;

  // Locate the target (retry while it mounts/lays out; auto-skip if it never
  // appears — nav items are permission-gated, so some anchors won't exist).
  useEffect(() => {
    if (!visible || !step) {
      setRect(null);
      return;
    }
    setBubble(null);
    let raf = 0;
    let tries = 0;
    const locate = () => {
      const el = document.querySelector(
        `[data-tour="${step.anchorId}"]`,
      ) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) {
          // Bring an off-screen anchor into view before measuring. Anchors can
          // live below the fold or inside an inner scroller (e.g. the sidebar's
          // .qba-scroll); scrollIntoView walks every scrollable ancestor, so a
          // single instant scroll handles both. Without it the fixed-position
          // spotlight is drawn at the anchor's real (off-screen/clipped) coords
          // while the bubble is clamped into the viewport — highlight and bubble
          // end up on different elements.
          const outOfView =
            r.top < MARGIN || r.bottom > window.innerHeight - MARGIN;
          if (outOfView) {
            el.scrollIntoView({ block: "center", inline: "nearest" });
            setRect(el.getBoundingClientRect());
          } else {
            setRect(r);
          }
          return;
        }
      }
      tries += 1;
      if (tries > MAX_RETRY) {
        next();
        return;
      }
      raf = requestAnimationFrame(locate);
    };
    locate();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step?.anchorId, stepIndex]);

  // Keep the spotlight glued to the target on scroll (capture, for inner
  // scrollers like brp-scroll) and resize.
  useEffect(() => {
    if (!visible || !step) return;
    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = document.querySelector(
          `[data-tour="${step.anchorId}"]`,
        ) as HTMLElement | null;
        if (el) setRect(el.getBoundingClientRect());
      });
    };
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step?.anchorId, stepIndex]);

  // Two-pass placement: measure the rendered bubble, then place + clamp it.
  useLayoutEffect(() => {
    if (!rect || !bubbleRef.current) return;
    const bw = bubbleRef.current.offsetWidth;
    const bh = bubbleRef.current.offsetHeight;
    setBubble(computeBubble(rect, bw, bh, step?.placement || "auto"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect, stepIndex]);

  // Keyboard: Esc = skip, →/Enter = next, ← = prev.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") stop({ completed: false });
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, stepIndex]);

  if (!visible || !step || !rect) return null;

  const isLast = stepIndex === total - 1;

  return createPortal(
    <>
      <div className="tov-mask" />
      <div
        className="tov-spot"
        style={{
          top: rect.top - SPOT_PAD,
          left: rect.left - SPOT_PAD,
          width: rect.width + SPOT_PAD * 2,
          height: rect.height + SPOT_PAD * 2,
        }}
      />
      <div
        ref={bubbleRef}
        className={`tov-bubble ${bubble ? "tov-bubble--ready" : ""}`}
        style={bubble ? { top: bubble.top, left: bubble.left } : { top: 0, left: 0 }}
        role="dialog"
        aria-modal="true"
        aria-label={i18n.t(step.titleKey)}
      >
        <button
          type="button"
          className="tov-x"
          onClick={() => stop({ completed: false })}
          aria-label={i18n.t("close")}
        >
          ✕
        </button>
        <div className="tov-title">{i18n.t(step.titleKey)}</div>
        <p className="tov-body">{i18n.t(step.bodyKey)}</p>
        <div className="tov-foot">
          <div className="tov-dots">
            {activeTour?.steps.map((s, i) => (
              <span
                key={s.id}
                className={`tov-dot ${i === stepIndex ? "is-active" : ""}`}
              />
            ))}
          </div>
          <div className="tov-actions">
            <button
              type="button"
              className="tov-skip"
              onClick={() => stop({ completed: false })}
            >
              {i18n.t("skip")}
            </button>
            {stepIndex > 0 ? (
              <button
                type="button"
                className="tov-btn tov-btn--ghost"
                onClick={prev}
              >
                {i18n.t("previous")}
              </button>
            ) : null}
            <button
              type="button"
              className="tov-btn tov-btn--primary"
              onClick={next}
            >
              {isLast ? i18n.t("tour_finish") : i18n.t("next")}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default TourOverlay;
